import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DocumentLineType } from 'projects/erp-app/src/app/base/documents/document-lines/document-line.model';
import { ConfigurationsService } from 'projects/erp-app/src/app/base/modules/configurations/configurations.service';
import { TaxRate } from 'projects/erp-app/src/app/base/tax-rates/tax-rate';
import { Unit } from 'projects/erp-app/src/app/base/units/unit.model';
import { Product } from 'projects/erp-app/src/app/products/products/product.model';
import { ConfirmModalComponent } from 'projects/libraries/syslink-components/src/public-api';
import { PurchaseDocumentLineDiscountOperationsService } from '../../purchase-document-line-discount-operations/purchase-document-line-discount-operations.service';
import { PurchaseDocumentLine } from '../../purchase-document-lines/purchase-document-line.model';
import { PurchaseDocumentLinesService } from '../../purchase-document-lines/purchase-document-lines.service';
import { PurchaseDocument } from '../purchase-document.model';
import { PurchaseDocumentsService } from '../purchase-documents.service';
import { SyslinkColumn } from 'projects/libraries/syslink-components/src/lib/helpers/SyslinkColumn';
import { getNew } from 'projects/libraries/syslink-components/src/lib/helpers/tools';
import { ViewComponent } from 'projects/libraries/syslink-components/src/lib/helpers/view/view.component';
import { PurchaseDocumentStatus } from '../../purchase-document-statuses/purchase-document-status.model';

@Component({
  selector: 'app-purchase-document-content',
  templateUrl: './purchase-document-content.component.html',
  styleUrls: ['./purchase-document-content.component.scss']
})
export class PurchaseDocumentContentComponent<
  TPurchaseDocument extends PurchaseDocument,
  TPurchaseDocumentService extends PurchaseDocumentsService<TPurchaseDocument, PurchaseDocumentStatus>,
  TPurchaseDocumentLine extends PurchaseDocumentLine,
  TPurchaseDocumentLineService extends PurchaseDocumentLinesService<PurchaseDocumentLine>
> extends ViewComponent {
  @Input() public element: TPurchaseDocument = <TPurchaseDocument>getNew<TPurchaseDocument>();
  @Output() public elementChange: EventEmitter<TPurchaseDocument> = new EventEmitter<TPurchaseDocument>();

  @Input() public PurchaseDocumentService: TPurchaseDocumentService = <TPurchaseDocumentService>getNew<TPurchaseDocumentService>();
  @Input() public PurchaseDocumentLinesService: TPurchaseDocumentLineService = <TPurchaseDocumentLineService>getNew<TPurchaseDocumentLineService>();

  @Input() public selectedKey: number[] = [];
  @Output() public elementLineChange: EventEmitter<TPurchaseDocumentLine> = new EventEmitter<TPurchaseDocumentLine>();
  @Output() public selectedKeyChange: EventEmitter<number[]> = new EventEmitter<number[]>();

  @ViewChild('createNewProduct') createNewProduct: ConfirmModalComponent = new ConfirmModalComponent();

  @Input() disabled: boolean = false;
  @Input() public reportType: String = '';
  @Input() public subModuleCode: string = '';
  @Input() public columns: SyslinkColumn[] = [];

  public isAddingLine: boolean = false;

  constructor(
    public activatedRoute: ActivatedRoute,
    private PurchaseDocumentLineDiscountOperationsService: PurchaseDocumentLineDiscountOperationsService,
    private configurationsService: ConfigurationsService,
    private translateService: TranslateService
  ) {
    super();
  }

  // Add line
  // --------
  public async onAddLine(type: 'text' | 'title' | 'various' | 'page' | 'product' | 'workTime', product?: Product): Promise<void> {
    if (this.element.Id == null) return;
    if (this.isAddingLine) return;

    this.isAddingLine = true;
    let line: TPurchaseDocumentLine;

    switch (type) {
      case 'text':
        line = await this.onAddTextClicked();
        break;

      case 'title':
        line = await this.onAddTitleClicked();
        break;

      case 'various':
        line = await this.onAddVariousClicked();
        break;

      case 'page':
        line = await this.onAddPageClicked();
        break;

      case 'product':
        if (!product) return;
        line = await this.onAdvancedProductSelected(product);
        break;

      default:
        return;
    }

    this.elementLineChange.emit(line);
    this.isAddingLine = false;
  }

  private async onAddTextClicked(): Promise<TPurchaseDocumentLine> {
    const lineNo = await this.getLineNo();
    const parent: TPurchaseDocumentLine | undefined = await this.getParentId();
    const line = await this.PurchaseDocumentLinesService.insert(this.PurchaseDocumentLinesService.format({
      HeaderId: this.element,
      LineNo: lineNo,
      LineType: DocumentLineType.text,
      ParentId: parent
    }));
    return line;
  }

  private async onAddTitleClicked(): Promise<TPurchaseDocumentLine> {
    const reference = this.getReference("Post");
    const lineNo = await this.getLineNo();
    const line = await this.PurchaseDocumentLinesService.insert(this.PurchaseDocumentLinesService.format({
      HeaderId: this.element,
      LineNo: lineNo,
      LineType: DocumentLineType.post,
      Reference: reference
    }));
    return line;
  }

  private async onAddVariousClicked(): Promise<TPurchaseDocumentLine> {
    const reference = this.getReference("Various");
    const parent: TPurchaseDocumentLine | undefined = await this.getParentId();
    const lineNo = await this.getLineNo();
    const quantity = await this.configurationsService.getConfigurationAsNumber('variousDefaultQuantity', 'Purchases.General', 'Purchases');
    const unitId = new Unit({ Id: await this.configurationsService.getConfigurationAsNumber('variousDefaultUnit', 'Purchases.General', 'Purchases') });
    const taxRateId = new TaxRate({ Id: await this.configurationsService.getConfigurationAsNumber('variousDefaultTaxRate', 'Purchases.General', 'Purchases') });

    const line = await this.PurchaseDocumentLinesService.insert(this.PurchaseDocumentLinesService.format({
      HeaderId: this.element,
      LineNo: lineNo,
      LineType: DocumentLineType.various,
      Reference: reference,
      ParentId: parent,
      Quantity: quantity,
      UnitId: unitId,
      TaxRateId: taxRateId
    }));
    return line;
  }

  private async onAddPageClicked(): Promise<TPurchaseDocumentLine> {
    const reference = this.getReference("Page");
    const parent: TPurchaseDocumentLine | undefined = await this.getParentId();
    const lineNo = await this.getLineNo();
    const line = await this.PurchaseDocumentLinesService.insert(this.PurchaseDocumentLinesService.format({
      HeaderId: this.element,
      LineNo: lineNo,
      LineType: DocumentLineType.page,
      Reference: reference,
      ParentId: parent
    }));
    return line;
  }

  // Product
  // -------
  public onProductNotFound(textSearched: string) {
    if (this.createNewProduct) this.createNewProduct.open({ 'text': textSearched });
  }

  public async onCreateProduct() {
    if (this.createNewProduct) this.createNewProduct.close();

    const url = this.router.serializeUrl(this.router.createUrlTree(["/products/products/new"], {
      relativeTo: this.activatedRoute, queryParams: {
        ProductName: this.createNewProduct?.data?.text ? this.createNewProduct.data.text : null,
      }
    }));
    window.open(url, '_blank');
  }

  private async onAdvancedProductSelected(product: Product): Promise<TPurchaseDocumentLine> {
    const parent: TPurchaseDocumentLine | undefined = await this.getParentId();
    const lineNo = await this.getLineNo();
    const quantity = await this.configurationsService.getConfigurationAsNumber('variousDefaultQuantity', 'Purchases.General', 'Purchases');
    const line = await this.PurchaseDocumentLinesService.insert(this.PurchaseDocumentLinesService.format({
      HeaderId: this.element,
      LineNo: lineNo,
      LineType: DocumentLineType.product,
      ParentId: parent,
      ProductId: product,
      Quantity: quantity
    }));
    return line;
  }

  //-------------------------------------------------------------------------

  // Delete Line
  // -----------
  public async onDeleteLine(event: any) {
    if (!event.key) return;
    let line = this.element.Lines?.find(s => s.Id == event.key);
    if (line && line.Id != null && line.ParentId != null && line.ParentId.Id) {
      var oldParentId = line.ParentId.Id;
      line.ParentId = undefined;
      await this.PurchaseDocumentLinesService.update(line.Id, this.PurchaseDocumentLinesService.format(line));
      await this.PurchaseDocumentLinesService.updateParentPrice(oldParentId);
    }
    await this.PurchaseDocumentLinesService.remove(event.key);
    this.elementLineChange.emit(event.data);
  }
  //-------------------------------------------------------------------------

  // Update Line
  // -----------
  public async onUpdateLine(event: any) {

    let line: TPurchaseDocumentLine = await this.PurchaseDocumentLinesService.getInstance({ ...event.oldData, ...event.newData });
    // TODO Update best method  #4700
    if (event.newData.Quantity != undefined) {
      line = { ...event.newData, Id: line.Id,Discount: line.Discount,ForcedPrice: line.ForcedPrice,LineType: line.LineType };
    }
    if (line.Discount && line.Discount.Id) {
      await this.PurchaseDocumentLineDiscountOperationsService.update(line.Discount.Id, line.Discount);
    }

    if (line.ForcedPrice == null && (line.LineType == "product" || line.LineType == "various")) {
      line.ForcedPrice = 0;
    }
    if (line.Id) {
      await this.PurchaseDocumentLinesService.update(line.Id, this.PurchaseDocumentLinesService.format(line));
    }
    if (line.ParentId != null && line.ParentId.Id != null)
      await this.PurchaseDocumentLinesService.updateParentPrice(line.ParentId.Id);

    this.elementLineChange.emit(line);
  }
  //-------------------------------------------------------------------------

  public onSelectedKeyChange(selectedKeys: number[]) {
    this.selectedKey = selectedKeys;
    this.selectedKeyChange.emit(this.selectedKey);
  }


  public getReference(label: string) {
    if (!this.element.Lines)
      return this.translateService.instant(label);
    let count = (this.element.Lines.filter((e: any) => e.LineType == label.toLowerCase()).length);
    return this.translateService.instant(label) + ' ' + (count + 1);
  }

  public async getLineNo() {
    if (!this.element.Lines) return 1;

    var result: number = this.element.Lines.length;

    if (this.selectedKey.length !== 0) {
      result = this.element.Lines.find(l => l.Id === this.selectedKey[0])?.LineNo || 0;
    }

    return result + 1;
  }

  public async getParentId(): Promise<TPurchaseDocumentLine | undefined> {
    if (this.selectedKey.length != 0) {
      let line = this.element.Lines?.find(l => l.Id === this.selectedKey[0]);
      var parent = line && (line.LineType === DocumentLineType.post ? line.Id : line.ParentId?.Id) ? await this.PurchaseDocumentLinesService.getInstance({
        Id: (line.LineType == DocumentLineType.post ? line.Id : line.ParentId?.Id),
        HeaderId: await this.PurchaseDocumentService.format(this.element)
      }) : undefined;

      if (parent) {
        parent.HeaderId = await this.PurchaseDocumentService.format(this.element);
      }

      return parent;
    }
    if (this.element.Lines && this.element.Lines.length > 0) {
      let parents = this.element.Lines[this.element.Lines.length - 1];
      if (parents != null && parents.LineType == "post")
        return await this.PurchaseDocumentLinesService.getInstance({
          Id: parents.Id,
          HeaderId: await this.PurchaseDocumentService.format(this.element)
        });
      if (parents.ParentId != null && parents.ParentId.Id != null)
        return await this.PurchaseDocumentLinesService.getInstance({
          Id: parents.ParentId.Id,
          HeaderId: await this.PurchaseDocumentService.format(this.element)
        });
    }
    return undefined;
  }

  // Drag and drop
  // -------------
  public async OnReorder(e: any) {
    if (!this.element.Id) return;
    //this.ngxUiLoaderService.start();
    let targetElementIsBefore = e.fromIndex > e.toIndex; // Target element is the element before if the line is pulled up and the element after if it is dragged down.
    let realLines: TPurchaseDocumentLine[] = <TPurchaseDocumentLine[]>((await this.PurchaseDocumentService.findByID(this.element.Id)).Lines || []);
    var lines: TPurchaseDocumentLine[] = <TPurchaseDocumentLine[]>(this.element.Lines || []);
    var parent: TPurchaseDocumentLine | undefined = undefined;
    var toIndex = e.toIndex;
    if (e.dropInsideItem == true && targetElementIsBefore == true)
      toIndex++;

    this.arraymove(realLines, e.fromIndex, toIndex);
    if (toIndex > 0 && realLines[toIndex].LineType != "post") {
      if (realLines[toIndex - 1].LineType == "post")
        parent = realLines[toIndex - 1];
      else
        parent = <TPurchaseDocumentLine | undefined>(realLines[toIndex - 1].ParentId);
    }
    else
      parent = undefined;

    var oldParent: TPurchaseDocumentLine | undefined = <TPurchaseDocumentLine | undefined>(realLines[toIndex].ParentId);
    realLines[toIndex].ParentId = parent;
    const newline = realLines[toIndex];
    if (newline.Id != undefined) {
      await this.PurchaseDocumentLinesService.update(newline.Id, this.PurchaseDocumentLinesService.format(newline));
    }

    // Update LineNo
    // -------------
    for (let i = 0; i < realLines.length; i++) {
      realLines[i].LineNo = i + 1;
    }

    // Update lines
    // ------------
    for (let i = 0; i < realLines.length; i++) {
      if (realLines[i].Id == lines[i].Id) { }
      else {
        var localLine = this.PurchaseDocumentLinesService.format(realLines[i]);
        if (localLine.Id) {
          await this.PurchaseDocumentLinesService.update(localLine.Id, localLine)
          await this.PurchaseDocumentLinesService.changeLineNo(localLine, targetElementIsBefore);
          lines = <TPurchaseDocumentLine[]>((await this.PurchaseDocumentService.findByID(this.element.Id)).Lines || []);
        }
      }
    }
    for (let i = 0; i < realLines.length; i++) {
      if (realLines[i].Id == lines[i].Id) { }
      else {
        var localLine = this.PurchaseDocumentLinesService.format(realLines[i]);
        if (localLine.Id) {
          await this.PurchaseDocumentLinesService.update(localLine.Id, localLine)
          await this.PurchaseDocumentLinesService.changeLineNo(localLine, targetElementIsBefore);
          lines = <TPurchaseDocumentLine[]>((await this.PurchaseDocumentService.findByID(this.element.Id)).Lines || []);
        }
      }
    }
    var lineId = realLines[toIndex];
    if (oldParent != realLines[toIndex].ParentId && oldParent != null && oldParent.Id != null) {
      await this.PurchaseDocumentLinesService.updateParentPrice(oldParent.Id);
    }
    if (lineId && lineId.ParentId && lineId.ParentId.Id != null) {
      await this.PurchaseDocumentLinesService.updateParentPrice(lineId.ParentId.Id);
    }
    this.elementLineChange.emit(e);
    //this.ngxUiLoaderService.stop();
  }

  public arraymove(arr: any, fromIndex: any, toIndex: any) {
    var element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }

}

